import React, { useEffect, useState } from 'react';
import { AppState, Platform, View, Text, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, useSegments } from 'expo-router';
import { isPushLikelyDeliverable, subscribeToPushStateChanges } from '@/lib/onesignal-push';
import { useCrew } from '@/context/CrewProvider';
import { trpc } from '@/trpc/react';
import colors from '@/constants/Colors';

// Defer to the push-permission banner when push delivery is broken.

export default function UnreadMessagesBanner() {
  if (Platform.OS === 'web') return null;

  const router = useRouter();
  const insets = useSafeAreaInsets();
  // expo-router types `useSegments()` as a tuple of literal strings which
  // makes `.includes('messages')` fail typecheck. Cast to `string[]` —
  // we only do an equality check below so the runtime semantics are unchanged.
  const segments = useSegments() as string[];
  const { currentCrewMember } = useCrew();
  const [pushOk, setPushOk] = useState<boolean>(isPushLikelyDeliverable);

  // Re-uses the same query as useBadgeCount and the drawer message indicator.
  // React Query deduplicates the call, so this adds zero additional server
  // load even though three consumers read it.
  const { data: unreadCount = 0 } = trpc.messages.getUnreadMessageCount.useQuery(
    { crew_id: currentCrewMember?.id! },
    {
      enabled: !!currentCrewMember?.id,
      refetchInterval: 30000,
    },
  );

  useEffect(() => {
    const refresh = () => setPushOk(isPushLikelyDeliverable());
    const unsubscribe = subscribeToPushStateChanges(() => refresh());
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') refresh();
    });
    return () => {
      unsubscribe();
      sub.remove();
    };
  }, []);

  // Hide when:
  //  - we have no crew member (still loading or signed out)
  //  - there's nothing to nag about (unread === 0)
  //  - the user is already on the messages tab (any sub-route, including a
  //    specific conversation) — the banner would be noise on the screen
  //    that already shows the same information
  //  - push notifications are broken — the push banner is more important
  //    and we never want to stack two banners
  if (!currentCrewMember?.id) return null;
  if (unreadCount <= 0) return null;
  if (segments.includes('messages')) return null;
  if (!pushOk) return null;

  const handleOpenMessages = () => {
    try {
      router.push('/(app)/messages' as never);
    } catch {
      // Route not available (rare — would mean we're not in the app stack).
    }
  };

  const label = unreadCount === 1 ? '1 new message' : `${unreadCount} new messages`;

  return (
    <TouchableOpacity
      onPress={handleOpenMessages}
      activeOpacity={0.85}
      accessibilityRole="button"
      accessibilityLabel={`${label}. Tap to open messages.`}
      style={{
        width: '100%',
        backgroundColor: colors.accent,
        paddingTop: insets.top,
        borderBottomColor: 'rgba(0,0,0,0.15)',
        borderBottomWidth: 1,
      }}
    >
      <View
        style={{
          paddingHorizontal: 16,
          paddingVertical: 10,
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <View
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: 'rgba(0,0,0,0.12)',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
          }}
        >
          <Ionicons name="chatbubble-ellipses" size={18} color="#000" />
        </View>

        <View style={{ flex: 1, paddingRight: 12 }}>
          <Text style={{ color: '#000', fontWeight: '700', fontSize: 14 }} numberOfLines={1}>
            {label}
          </Text>
          <Text
            style={{ color: 'rgba(0,0,0,0.7)', fontSize: 12, marginTop: 2 }}
            numberOfLines={1}
          >
            Tap to open your conversations.
          </Text>
        </View>

        <View
          style={{
            backgroundColor: '#000',
            paddingHorizontal: 12,
            paddingVertical: 6,
            borderRadius: 6,
          }}
        >
          <Text style={{ color: colors.accent, fontWeight: '700', fontSize: 12 }}>Open</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
}
